<!--
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
-->
<template>
  <div
    ref="FormInputControlContainer"
    class="form-group"
    :class="{static:disabled}"
    :data-qa="dataQa ? `${dataQa}_container` : null"
    @focus.capture="inputFocusIn"
    @blur.capture="inputFocusOut"
  >
    <label
      v-if="label"
      :data-qa="dataQa ? `${dataQa}_label` : null"
    >
      <svg-icon
        v-if="label.icon"
        :icon="label.icon"
        class="label-left-icon"
        :data-qa="dataQa ? `${dataQa}_label_left_icon` : null"
      />
      {{ label.text }}
    </label>
    <div
      class="position-relative"
      :data-qa="dataQa ? `${dataQa}_textarea_container` : null"
    >
      <div
        v-if="fakeInput"
        class="form-control"
        :class="hasRightIcon + ' ' + hasLeftIcon"
        v-on="listeners"
      >
        {{ value && value.trim().length > 0 ? value : placeholder }}
      </div>
      <template
        v-else-if="textarea"
      >
        <textarea
          ref="FormInputControl"
          v-model="value"
          class="form-control"
          :placeholder="placeholder"
          :disabled="disabled"
          :autocomplete="autocomplete"
          :style="{height: textAreaHeight}"
          :data-qa="dataQa"
          v-on="listeners"
        />
        <div
          v-if="textareaAutoHeight"
          ref="TextAreaAutoHeightMirror"
          class="_text-area-auto-height-mirror"
        />
      </template>
      <input
        v-else
        ref="FormInputControl"
        :max="max"
        :maxlength="maxlength"
        :type="type"
        class="form-control"
        :class="hasRightIcon + ' ' + hasLeftIcon"
        :placeholder="placeholder"
        :value="value"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :data-qa="dataQa"
        v-on="listeners"
      >
      <svg-icon
        v-if="lefticon"
        :icon="lefticon"
        class="lefticon"
        :data-qa="dataQa ? `${dataQa}_left_icon` : null"
        @mousedown.native="leftIconClick"
      />
      <svg-icon
        v-if="showRightIcon"
        :icon="righticon"
        class="righticon"
        :style="showRightIcon ? 'display: block;' : ''"
        :data-qa="dataQa ? `${dataQa}_right_icon` : null"
        @mousedown.native="rightIconClick"
      />
    </div>
    <small
      class="form-text status-message"
      :data-qa="dataQa ? `${dataQa}_message_container` : null"
    >Error! Please check</small>
  </div>
</template>

<script>
export default {
  name: 'FormInput',
  props: {
    autocomplete: {
      type: String,
      default: 'new-password'
    },
    fakeInput: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number],
      default: null
    },
    label: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    lefticon: {
      type: String,
      default: null
    },
    righticon: {
      type: String,
      default: null
    },
    righticononfocusonly: {
      type: Boolean,
      default: true
    },
    rightIconAlways: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    trim: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    textareaAutoHeight: {
      type: Boolean,
      default: true
    },
    dataQa: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      focused: false,
      textAreaHeight: '41px'
    };
  },
  computed: {
    listeners () {
      return {
        ...this.$listeners,
        input: evt => {
          this.$emit('input', this.trim ? evt.target.value.trim() : evt.target.value);
        },
        focus: evt => {
          this.$emit('focus', evt);
        },
        blur: evt => {
          this.$emit('blur', evt);
        }
      };
    },
    hasRightIcon () {
      return this.righticon ? 'has-right-icon' : '';
    },
    hasLeftIcon () {
      return this.lefticon ? 'has-left-icon' : '';
    },
    showRightIcon () {
      return this.rightIconAlways ? true : this.righticononfocusonly ? this.focused && this.righticon && this.value : this.righticon && this.value;
    }
  },
  watch: {
    disabled (newValue, oldValue) {
      if (this.$refs.FormInputControlContainer) {
        if (newValue) {
          this.$refs.FormInputControlContainer.setAttribute('contenteditable', 'false');
        } else {
          this.$refs.FormInputControlContainer.removeAttribute('contenteditable');
        }
      }
    },
    value (newValue, oldValue) {
      this.setTextareaHeight();
    }
  },
  mounted () {
    if (this.disabled && this.$refs.FormInputControlContainer) {
      this.$refs.FormInputControlContainer.setAttribute('contenteditable', 'false');
    }
  },
  methods: {
    blurInput () {
      if (this.$refs.FormInputControl) {
        this.$refs.FormInputControl.blur();
        this.$emit('onInputBlur');
      }
    },
    focusInput () {
      if (this.$refs.FormInputControl) {
        this.$refs.FormInputControl.focus();
        this.$emit('onInputFocus');
      }
    },
    inputFocusIn () {
      this.focused = true;
      this.focusInput();
    },
    inputFocusOut () {
      this.blurInput();
      this.focused = false;
    },
    leftIconClick (evt) {
      this.focusInput();
      this.$emit('leftIconClick', evt);
    },
    rightIconClick (evt) {
      this.focusInput();
      this.$emit('rightIconClick', evt);
    },
    clear () {
      this.value = null;
    },
    setTextareaHeight () {
      if (this.textarea && this.$refs.FormInputControl && this.textareaAutoHeight && this.$refs.TextAreaAutoHeightMirror) {
        // Style the mirror
        this.$refs.TextAreaAutoHeightMirror.style.padding = getComputedStyle(this.$refs.FormInputControl, null).getPropertyValue('padding');
        this.$refs.TextAreaAutoHeightMirror.style.width = getComputedStyle(this.$refs.FormInputControl, null).getPropertyValue('width');
        this.$refs.TextAreaAutoHeightMirror.style.fontFamily = getComputedStyle(this.$refs.FormInputControl, null).getPropertyValue('font-family');
        this.$refs.TextAreaAutoHeightMirror.style.fontSize = getComputedStyle(this.$refs.FormInputControl, null).getPropertyValue('font-size');
        this.$refs.TextAreaAutoHeightMirror.style.lineHeight = getComputedStyle(this.$refs.FormInputControl, null).getPropertyValue('line-height');

        this.$refs.TextAreaAutoHeightMirror.innerHTML = String(this.$refs.FormInputControl.value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />') + '!<br />';
        this.textAreaHeight = `${this.$refs.TextAreaAutoHeightMirror.clientHeight}px`;
      }
    }
  }
};
</script>

<style scoped>
._text-area-auto-height-mirror {
  display: block;
  word-wrap: break-word;
  white-space: normal;
  visibility: hidden;
  position: absolute;
  top: -10000px;
  left: -10000px;
}
</style>
